import { computed, effect, inject, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { AppError, SpinnerService } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';

import {
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '@kr/core';
import { DictUser, WalletCard } from '@kr/models';

import { WALLET_USERS_API } from './api';

interface WalletUsersState {
  users: DictUser[];
  userIds: number[];
}

const initialState: WalletUsersState = {
  users: [],
  userIds: [],
};

export const WalletUsersStore = signalStore(
  withState(initialState),
  withRequestStatus(['getUsers', 'getUserIds', 'addUsers', 'removeUsers']),
  withRequestHandler(),
  withProps(store => ({
    store,
    walletUsersService: inject(WALLET_USERS_API),
    storeHelperService: inject(StoreHelperService),
    spinnerService: inject(SpinnerService),
  })),
  withComputed(({ users, userIds }) => ({
    selectedUsers: computed(() => {
      const filteredUsers = users().filter(user => userIds().includes(user.id));

      return filteredUsers.sort((a, b) => userIds().indexOf(a.id) - userIds().indexOf(b.id));
    }),
  })),
  withMethods(({ store, walletUsersService }) => ({
    getUsers: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getUsers'));
        return walletUsersService.getUsers().pipe(
          tapResponse({
            next: ({ users }) => {
              patchState(
                store,
                { users: users.map(user => ({ ...user, name: `${user.firstName} ${user.lastName}` })) },
                setSuccessStatusFor('getUsers'),
              );
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getUsers', error)),
          }),
        );
      }),
    ),

    getUserIds: rxMethod<WalletCard>(
      switchMap(wallet => {
        patchState(store, setPendingStatusFor('getUserIds'));
        return walletUsersService.getUserIds(wallet).pipe(
          tapResponse({
            next: ({ sharedUserIds }) => {
              patchState(store, { userIds: sharedUserIds }, setSuccessStatusFor('getUserIds'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getUserIds', error)),
          }),
        );
      }),
    ),

    addUsers: rxMethod<{ wallet: WalletCard; userIds: number[] }>(
      switchMap(({ wallet, userIds }) => {
        patchState(store, setPendingStatusFor('addUsers'));
        return walletUsersService.addUsers(wallet, userIds).pipe(
          tapResponse({
            next: () => patchState(store, { userIds: [...store.userIds(), ...userIds] }, setSuccessStatusFor('addUsers')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('addUsers', error)),
          }),
        );
      }),
    ),

    removeUsers: rxMethod<{ wallet: WalletCard; userIds: number[] }>(
      switchMap(({ wallet, userIds }) => {
        patchState(store, setPendingStatusFor('removeUsers'));
        return walletUsersService.removeUsers(wallet, userIds).pipe(
          tapResponse({
            next: () =>
              patchState(store, { userIds: store.userIds().filter(id => !userIds.includes(id)) }, setSuccessStatusFor('removeUsers')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('removeUsers', error)),
          }),
        );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      store.getUsers();
      store.spinnerService.observe(toObservable(store.isLoading));

      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);
